import { z } from 'zod';
import { StatusTypeSchema } from '../enums/status.js';
import { AccountAuditSchema } from '../types/account.js';
import { applicationSchema } from '../types/applications.js';
import { chatContextLayoutSchema } from '../types/chat-contexts.js';
import { FeatureDetailSchema } from '../types/features.js';
import { UserAuditSchema } from '../types/user.js';

export namespace CreateApplication {
  export const requestBodySchema = z.object({
    application: applicationSchema.omit({
      id: true,
      createdOn: true,
      identifier: true,
      createdBy: true,
      status: true,
    }),
  });
  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.object({ application: applicationSchema });
  export type Response = z.infer<typeof responseSchema>;
}

export namespace GetApplications {
  export const responseSchema = z.object({
    applications: z.array(applicationSchema),
  });
  export type Response = z.infer<typeof responseSchema>;
}

export namespace GetApplication {
  export const paramsSchema = z.object({
    id: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const responseSchema = z.object({
    application: applicationSchema.extend({
      brands: z.array(
        z.object({
          id: z.number(),
          name: z.string(),
        })
      ),
    }),
    features: z.array(FeatureDetailSchema),
  });
  export type Response = z.infer<typeof responseSchema>;
}

export namespace GetApplicationUsers {
  export const paramsSchema = z.object({
    id: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const responseSchema = z.object({
    users: z.array(UserAuditSchema),
  });
  export type Response = z.infer<typeof responseSchema>;
}

export namespace GetApplicationAccounts {
  export const paramsSchema = z.object({
    id: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const responseSchema = z.object({
    accounts: z.array(AccountAuditSchema),
  });
  export type Response = z.infer<typeof responseSchema>;
}
export namespace UpdateApplication {
  export const paramsSchema = z.object({
    id: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({
    application: applicationSchema.omit({
      id: true,
      createdOn: true,
      identifier: true,
      createdBy: true,
      status: true,
    }),
  });
  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.object({ application: applicationSchema });
  export type Response = z.infer<typeof responseSchema>;
}

export namespace UpdateApplicationStatus {
  export const paramsSchema = z.object({
    id: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({
    status: StatusTypeSchema,
  });
  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.object({ application: applicationSchema });
  export type Response = z.infer<typeof responseSchema>;
}

export const getApplicationChatContextsRequestParamsSchema = z.object({
  applicationId: z.coerce.number(),
});
export const getApplicationChatContextsResponseBodySchema = z.object({
  chatContexts: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      createdOn: z.coerce.date(),
      // Change: Return explicit ordering and row version metadata.
      ordinal: z.number().int().positive(),
      updatedOn: z.coerce.date(),
      pageId: z.number().nullable().optional(),
      layout: chatContextLayoutSchema.default('full_page'),
    })
  ),
});

export const addApplicationChatContextRequestParamsSchema = z.object({
  applicationId: z.coerce.number(),
});
export const addApplicationChatContextRequestBodySchema = z.object({
  chatContextId: z.number(),
});

export const deleteApplicationChatContextRequestParamsSchema = z.object({
  applicationId: z.coerce.number(),
  chatContextId: z.coerce.number(),
});
// Change: Optional optimistic concurrency token for delete operations.
export const deleteApplicationChatContextRequestQuerySchema = z.object({
  updatedOn: z.coerce.date().optional(),
});

export const updateApplicationChatContextPageRequestParamsSchema = z.object({
  applicationId: z.coerce.number(),
  chatContextId: z.coerce.number(),
});
export const updateApplicationChatContextPageRequestBodySchema = z.object({
  pageId: z.number().nullable().optional(),
  layout: chatContextLayoutSchema.optional(),
  // Change: Support reordering and optimistic concurrency in PATCH payload.
  ordinal: z.number().int().positive().optional(),
  updatedOn: z.coerce.date().optional(),
});

export const landingPagePromptSourceSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('free-text'),
    value: z.string(),
  }),
  z.object({
    type: z.literal('prompt'),
    value: z.object({
      promptIdentifier: z.uuid(),
    }),
  }),
]);

export const applicationLandingPagePromptSchema = z.object({
  id: z.number(),
  applicationId: z.number(),
  promptId: z.number().nullable().optional(),
  promptIdentifier: z.string().uuid().nullable().optional(),
  chatContextId: z.number().nullable().optional(),
  chatContextName: z.string().nullable().optional(),
  sourcePromptOrdinal: z.number().int().positive().nullable().optional(),
  ordinal: z.number().int().positive(),
  displayLabel: z.string(),
  promptBody: z.string(),
  source: landingPagePromptSourceSchema,
});

export const availableApplicationLandingPagePromptSchema = z.object({
  promptId: z.number(),
  promptIdentifier: z.string().uuid(),
  displayLabel: z.string(),
  promptBody: z.string(),
});

export const availableApplicationLandingPagePromptLegacySchema = z.object({
  chatContextId: z.number(),
  chatContextName: z.string(),
  sourcePromptOrdinal: z.number().int().positive(),
  displayLabel: z.string(),
  promptBody: z.string(),
  source: landingPagePromptSourceSchema,
});

export const getApplicationLandingPagePromptsRequestParamsSchema = z.object({
  applicationId: z.coerce.number(),
});

export const getApplicationLandingPagePromptsResponseBodySchema = z.object({
  prompts: z.array(applicationLandingPagePromptSchema),
});

export const getAvailableApplicationLandingPagePromptsRequestParamsSchema = z.object({
  applicationId: z.coerce.number(),
});

export const getAvailableApplicationLandingPagePromptsResponseBodySchema = z.object({
  prompts: z.array(availableApplicationLandingPagePromptSchema),
});

export const getAvailableApplicationLandingPageChatContextPromptsResponseBodySchema = z.object({
  prompts: z.array(availableApplicationLandingPagePromptLegacySchema),
});

export const addApplicationLandingPagePromptRequestParamsSchema = z.object({
  applicationId: z.coerce.number(),
});

export const addApplicationLandingPagePromptRequestBodySchema = z.object({
  promptId: z.number(),
});

export const updateApplicationLandingPagePromptRequestParamsSchema = z.object({
  applicationId: z.coerce.number(),
  landingPagePromptId: z.coerce.number(),
});

export const updateApplicationLandingPagePromptRequestBodySchema = z.object({
  ordinal: z.number().int().positive(),
});

export const deleteApplicationLandingPagePromptRequestParamsSchema = z.object({
  applicationId: z.coerce.number(),
  landingPagePromptId: z.coerce.number(),
});

export const reorderApplicationLandingPagePromptsRequestParamsSchema = z.object({
  applicationId: z.coerce.number(),
});

export const reorderApplicationLandingPagePromptsRequestBodySchema = z.object({
  orders: z
    .array(
      z.object({
        landingPagePromptId: z.number(),
        ordinal: z.number().int().positive(),
      })
    )
    .min(1),
});
